import { useState } from "react";
import { AxiosResponse } from "axios";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { OrganizationApi, OrganizationTypeApi } from "../../../../../../services/Api/types";

interface Props {
	onHide: () => void;
	changeOrganizationType?: (organizationId: number, type: OrganizationTypeApi) => Promise<AxiosResponse>;
	refresh: () => Promise<void>;
	organization: OrganizationApi;
}

const options = ["Demo", "Prod", "Test"].map((option) => ({
	value: option.toUpperCase(),
	label: option,
}));

const OrganizationUpdateTypeModal = ({ onHide, changeOrganizationType, refresh, organization }: Props) => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const { id: organizationId, type: organizationType } = organization;
	const {
		control,
		formState: { errors },
		formState,
		handleSubmit,
	} = useForm<{ organizationType: OrganizationTypeApi }>({
		defaultValues: { organizationType: organizationType as OrganizationTypeApi },
	});

	const changeOrganizationTypeHandler = handleSubmit(async (data) => {
		if (!changeOrganizationType || !organizationId) return;

		try {
			setLoading(true);
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await changeOrganizationType(organizationId, data.organizationType);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await refresh();
			onHide();
		} catch (err) {
			handleError.alert(err, addFlash);
		} finally {
			setLoading(false);
		}
	});

	return (
		<Modal show onHide={onHide}>
			<FormDirty formState={formState} onSubmit={changeOrganizationTypeHandler}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t("modules.organization.action.change_organization_type.title", { ns: "admin" })}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormSelectGroup
						control={control}
						name="organizationType"
						errors={errors}
						options={options}
						label={t("modules.organization.field.organization_type.title", { ns: "admin" })}
						defaultValue={{
							value: organizationType.toUpperCase(),
							label: `${organizationType.substring(0, 1)}${organizationType.substring(1).toLowerCase()}`,
						}}
						isClearable={false}
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit">
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default OrganizationUpdateTypeModal;
